import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-keys";

import { useUserEntity } from "@/contexts/user-entity-context";
import { getLocalizedName } from "@/lib/localization";
import { formatDate } from "@/lib/formatting";
import { getLoad, getLoadStatusLabel } from "@/lib/api/loads";
import { fromIntlLanguage } from "@/lib/i18n";
import { BackButton } from "@/components/ui/back-button";
import { Button } from "@/components/ui/button";
import { ErrorState, PageHeading, Value } from "@/components/ui/text";
import { Icon } from "@/components/ui/icons";
import { PageHeader } from "@/components/ui/page-header";
import { PageTitle } from "@/contexts/page-title-context";
import { Skeleton } from "@/components/ui/skeleton";
import { EntityStatusBadge } from "@/components/shared/entity-status-badge";
import { HStack } from "@/components/ui/h-stack";
import { VStack } from "@/components/ui/v-stack";
import {
  LoadDetailsSkeleton,
  LoadDetailsView,
  computeLoadDetailsPolicy,
  type LoadDetailsData,
  type ViewerRole,
} from "@/components/load-detail";

function LoadBoardLoadDetailsPage() {
  const params = Route.useParams();
  const router = useRouter();
  const language = fromIntlLanguage(useTranslation().i18n.language);
  const { t: tCommon } = useTranslation("common");
  const { t: tLoads } = useTranslation("loads");
  const { t: tNav } = useTranslation("nav");
  const { entity: userEntity } = useUserEntity();

  const {
    data: load = null,
    isLoading: loading,
    isError,
    error: loadError,
    refetch,
  } = useQuery({
    queryKey: queryKeys.loadBoardDetail(params.id),
    queryFn: async () => {
      const data = await getLoad(params.id);
      return data as LoadDetailsData;
    },
    enabled: !!params.id,
  });

  const errorMessage = isError
    ? loadError instanceof Error
      ? loadError.message
      : null
    : null;

  if (loading) {
    return (
      <VStack gap="xl" data-testid="load-board-load-details-page">
        <PageTitle>{tNav("loadBoard")}</PageTitle>
        <PageHeader>
          <HStack gap="lg">
            <BackButton onClick={() => router.history.back()}>
              {tCommon("back")}
            </BackButton>
            <VStack truncate gap="none">
              <PageHeading size="2xl">{tCommon("loading")}</PageHeading>
              <Value color="muted" size="sm" className="mt-1">
                {tCommon("loading")}
              </Value>
            </VStack>
            <Skeleton className="h-7 w-24" rounded="full" />
          </HStack>
        </PageHeader>
        <LoadDetailsSkeleton />
      </VStack>
    );
  }

  if (isError || !load) {
    return (
      <ErrorState
        title={tCommon("error")}
        description={
          errorMessage ??
          (isError ? tCommon("errorLoadingData") : tLoads("noLoadsTitle"))
        }
        action={
          <HStack align="stretch" gap="lg">
            <Button
              color="neutral"
              variant="outline"
              onClick={() => void refetch()}
            >
              {tCommon("retry")}
            </Button>
            <Button
              color="neutral"
              variant="outline"
              onClick={() => router.history.back()}
            >
              <Icon.ChevronLeft size="sm" className="mr-2" />
              {tCommon("back")}
            </Button>
          </HStack>
        }
        className="h-96"
      />
    );
  }

  const viewerRole: ViewerRole =
    userEntity?.type === "Broker" ? "broker" : "carrier";

  const policy = computeLoadDetailsPolicy({
    viewerRole,
    status: load.status,
    hasTrip: Boolean(load.trip),
  });

  const statusLabel = getLoadStatusLabel(load.status, tLoads);

  return (
    <VStack gap="xl" data-testid="load-board-load-details-page">
      <PageTitle>{tNav("loadBoard")}</PageTitle>

      <PageHeader>
        <HStack gap="lg">
          <BackButton onClick={() => router.history.back()}>
            {tCommon("back")}
          </BackButton>
          <VStack truncate gap="none">
            <PageHeading size="2xl">
              {load.load_id}: {getLocalizedName(load.load_type, language)}
            </PageHeading>
            <Value color="muted" size="sm" className="mt-1">
              {tLoads("created")}: {formatDate(load.created_at, language)}
            </Value>
          </VStack>
          <EntityStatusBadge entityType="load" status={load.status}>
            {statusLabel}
          </EntityStatusBadge>
        </HStack>
      </PageHeader>

      <LoadDetailsView data={load} policy={policy} />
    </VStack>
  );
}

export const Route = createFileRoute("/_app/load-board/$id")({
  component: LoadBoardLoadDetailsPage,
});
